/**
 * Habit lifecycle and check-in commands (phase-2b-habits.md HS1/HS3): pure
 * functions that validate a request against derived state and, on success,
 * return the event(s) to append. Shares the `CommandResult`/`DomainCommandError`
 * shape from `../event-log/command-error` with every other commands module
 * (objectives/review/rewards); same "no I/O" rule, ADR-0001 rule 2.
 *
 * Check-in-completion (AC-B14/AC-B15): `checkin.completed` fires on the
 * day's FIRST tap of any kind (habit or action) -- `withImplicitCheckin`
 * below is the shared helper every logging command in this module (and the
 * `completeAction` wrapper in create-domain-api.ts) uses to append it
 * alongside the triggering event in the same batch, per the contract doc
 * comment on `CheckinCompletedEvent`: "Duplicates for one occurredOn
 * collapse in derivation; the first earns the XP." That collapse rule lives
 * in `xp.ts`/`snapshot.ts` (only the first `checkin.completed` per day earns
 * XP); this module's job is simply to always emit one alongside the first
 * tap so the collapse rule has something to collapse -- emitting a
 * `checkin.completed` on EVERY tap (not just the first) is deliberate and
 * harmless: duplicates for one day are idempotent by construction (derived
 * XP/streak math only ever looks at "is there at least one for this day",
 * never a count), and computing "is this really the first tap today" here
 * would require an extra log scan on every single command for no behavioral
 * difference. See `hasCheckinToday` for the one place that scan happens, and
 * why it is a query-time streak concern, not a write-time gate.
 */

import type {
  CheckinCompletedEvent,
  ClockPort,
  CompassEvent,
  HabitArchivedEvent,
  HabitCategory,
  HabitCleanLoggedEvent,
  HabitCompletedEvent,
  HabitCreatedEvent,
  HabitDraft,
  HabitEditedEvent,
  HabitId,
  HabitLapseLoggedEvent,
  HabitPausedEvent,
  HabitPlan,
  HabitResumedEvent,
  HabitSkippedEvent,
  HabitTargetAdjustedEvent,
  IdPort,
  IsoDate,
  ObjectiveId,
  TargetAdjustment,
} from '../../contracts';
import { type CommandResult, domainError } from '../event-log/command-error';
import { eventsOfTypes, excludingReverted } from '../event-log/event-log';
import { buildHabitStates, type HabitState } from './state';

type Ports = { readonly clock: ClockPort; readonly id: IdPort };

// ---------------------------------------------------------------------------
// Habit lifecycle (HB-1..HB-4)
// ---------------------------------------------------------------------------

function assertValidPlan(plan: HabitPlan): void {
  if (plan.habitKind === 'build') {
    if (plan.schedule.kind === 'times_per_week' && plan.schedule.targetPerWeek < 1) {
      throw domainError(
        'invalid_input',
        `build habit targetPerWeek must be >= 1, got ${plan.schedule.targetPerWeek}`,
      );
    }
  } else {
    if (plan.schedule.limitPerWeek < 0) {
      throw domainError(
        'invalid_input',
        `avoid habit limitPerWeek must be >= 0, got ${plan.schedule.limitPerWeek}`,
      );
    }
  }
}

export function createHabit(
  draft: HabitDraft,
  ports: Ports,
): CommandResult<HabitCreatedEvent> {
  if (draft.name.trim().length === 0) {
    throw domainError('invalid_input', 'habit name must not be empty');
  }
  assertValidPlan(draft.plan);

  const event: HabitCreatedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'habit.created',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    habitId: ports.id.nextRawId() as HabitId,
    name: draft.name,
    category: draft.category,
    plan: draft.plan,
    // HB-1b/§1b item 7: accepted and persisted even though no UI in this
    // phase sets a non-null value -- "accept and store, don't expose" (see
    // AC-B13, verified independently of any UI path).
    objectiveId: draft.objectiveId,
  };

  return { events: [event] };
}

export function editHabit(
  events: readonly CompassEvent[],
  habitId: HabitId,
  changes: {
    readonly name?: string;
    readonly category?: HabitCategory;
    readonly objectiveId?: ObjectiveId | null;
  },
  ports: Ports,
): CommandResult<HabitEditedEvent> {
  requireHabit(events, habitId);
  if (changes.name !== undefined && changes.name.trim().length === 0) {
    throw domainError('invalid_input', 'habit name must not be empty');
  }

  const event: HabitEditedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'habit.edited',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    habitId,
    changes,
  };

  return { events: [event] };
}

/** Manual schedule change (settings/edit flow) -- always `source: 'manual'`, no realism bonus (HB-4). */
export function adjustTargetManually(
  events: readonly CompassEvent[],
  habitId: HabitId,
  adjustment: TargetAdjustment,
  ports: Ports,
): CommandResult<HabitTargetAdjustedEvent> {
  const state = requireHabit(events, habitId);
  if (adjustment.habitKind !== state.plan.habitKind) {
    throw domainError(
      'invalid_input',
      `adjustment habitKind (${adjustment.habitKind}) must match the habit's fixed kind (${state.plan.habitKind})`,
    );
  }
  assertValidPlan({ habitKind: adjustment.habitKind, schedule: adjustment.next } as HabitPlan);

  const event: HabitTargetAdjustedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'habit.target_adjusted',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    habitId,
    adjustment,
    source: 'manual',
    reviewWeek: null,
  };

  return { events: [event] };
}

export function pauseHabit(
  events: readonly CompassEvent[],
  habitId: HabitId,
  ports: Ports,
): CommandResult<HabitPausedEvent> {
  requireHabit(events, habitId);

  const event: HabitPausedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'habit.paused',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    habitId,
  };

  return { events: [event] };
}

export function resumeHabit(
  events: readonly CompassEvent[],
  habitId: HabitId,
  ports: Ports,
): CommandResult<HabitResumedEvent> {
  requireHabit(events, habitId);

  const event: HabitResumedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'habit.resumed',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    habitId,
  };

  return { events: [event] };
}

export function archiveHabit(
  events: readonly CompassEvent[],
  habitId: HabitId,
  ports: Ports,
): CommandResult<HabitArchivedEvent> {
  requireHabit(events, habitId);

  const event: HabitArchivedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'habit.archived',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    habitId,
  };

  return { events: [event] };
}

// ---------------------------------------------------------------------------
// Daily check-in logging (HB-6/HB-7)
// ---------------------------------------------------------------------------

export function completeHabit(
  events: readonly CompassEvent[],
  habitId: HabitId,
  date: IsoDate,
  ports: Ports,
): CommandResult<HabitCompletedEvent | CheckinCompletedEvent> {
  const state = requireActiveHabit(events, habitId);
  if (state.plan.habitKind !== 'build') {
    throw domainError('invalid_input', `habit ${habitId} is not a build habit`);
  }

  const event: HabitCompletedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'habit.completed',
    schemaVersion: 1,
    occurredOn: date,
    recordedAt: ports.clock.now(),
    habitId,
  };

  return withImplicitCheckin(events, [event], date, ports);
}

export function skipHabit(
  events: readonly CompassEvent[],
  habitId: HabitId,
  date: IsoDate,
  ports: Ports,
): CommandResult<HabitSkippedEvent | CheckinCompletedEvent> {
  const state = requireActiveHabit(events, habitId);
  if (state.plan.habitKind !== 'build') {
    throw domainError('invalid_input', `habit ${habitId} is not a build habit`);
  }

  const event: HabitSkippedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'habit.skipped',
    schemaVersion: 1,
    occurredOn: date,
    recordedAt: ports.clock.now(),
    habitId,
  };

  return withImplicitCheckin(events, [event], date, ports);
}

export function logClean(
  events: readonly CompassEvent[],
  habitId: HabitId,
  date: IsoDate,
  ports: Ports,
): CommandResult<HabitCleanLoggedEvent | CheckinCompletedEvent> {
  const state = requireActiveHabit(events, habitId);
  if (state.plan.habitKind !== 'avoid') {
    throw domainError('invalid_input', `habit ${habitId} is not an avoid habit`);
  }

  const event: HabitCleanLoggedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'habit.clean_logged',
    schemaVersion: 1,
    occurredOn: date,
    recordedAt: ports.clock.now(),
    habitId,
  };

  return withImplicitCheckin(events, [event], date, ports);
}

/**
 * Judgment-free by contract (R7.7, AC-B12): no confirmation, no XP
 * subtraction, never throws for "over allowance" -- the weekly allowance is
 * a read-model concern (`DueHabitView.allowance` can read "3 of 2 used"),
 * never a write-time gate. Logging a lapse still counts as "showed up" for
 * check-in purposes (AC-B14: "a day with only skips/lapses... still earns
 * checkinCompleted").
 */
export function logLapse(
  events: readonly CompassEvent[],
  habitId: HabitId,
  date: IsoDate,
  ports: Ports,
): CommandResult<HabitLapseLoggedEvent | CheckinCompletedEvent> {
  const state = requireActiveHabit(events, habitId);
  if (state.plan.habitKind !== 'avoid') {
    throw domainError('invalid_input', `habit ${habitId} is not an avoid habit`);
  }

  const event: HabitLapseLoggedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'habit.lapse_logged',
    schemaVersion: 1,
    occurredOn: date,
    recordedAt: ports.clock.now(),
    habitId,
  };

  return withImplicitCheckin(events, [event], date, ports);
}

/**
 * Explicit check-in completion command (used by `completeCheckin` in
 * create-domain-api.ts when called directly, and available for any future
 * "close the day" UI per phase-2b-habits.md §7 item 1's flagged
 * alternative). Idempotent in effect (AC-B15's collapse rule handles
 * duplicates at derivation time), but this function always appends one --
 * callers that want write-time dedup should check `hasCheckinToday` first,
 * mirroring `withImplicitCheckin`'s own check below. `events` is unused
 * (kept for call-site symmetry with every other command here, and so a
 * future write-time-dedup variant can add a lookup without an API change --
 * mirrors `createObjective`'s own unused `_events` parameter).
 */
export function completeCheckin(
  _events: readonly CompassEvent[],
  date: IsoDate,
  ports: Ports,
): CommandResult<CheckinCompletedEvent> {
  const event: CheckinCompletedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'checkin.completed',
    schemaVersion: 1,
    occurredOn: date,
    recordedAt: ports.clock.now(),
  };
  return { events: [event] };
}

/**
 * True when an active (non-reverted) `checkin.completed` event already
 * exists for `date` in `events`. `checkin.completed` is NOT in
 * `VALID_REVERT_TARGET_TYPES` (create-domain-api.ts) -- it is a derived
 * marker, not a user-facing log entry -- but `excludingReverted` is applied
 * anyway for defense-in-depth symmetry with every other status check in this
 * module, at zero cost since no revert can realistically target it.
 */
export function hasCheckinToday(
  events: readonly CompassEvent[],
  date: IsoDate,
): boolean {
  const active = excludingReverted(events);
  return eventsOfTypes(active, ['checkin.completed']).some((e) => e.occurredOn === date);
}

/**
 * Appends `checkin.completed` alongside `triggerEvents` when no active
 * check-in exists yet for `date` (AC-B14: "the first tap of the day"). The
 * check is against the log AS PASSED IN (before `triggerEvents`), which is
 * correct: `triggerEvents` themselves are never `checkin.completed`, so
 * whether one already exists for today does not change by adding them.
 */
function withImplicitCheckin<TEvent extends CompassEvent>(
  events: readonly CompassEvent[],
  triggerEvents: readonly TEvent[],
  date: IsoDate,
  ports: Ports,
): CommandResult<TEvent | CheckinCompletedEvent> {
  if (hasCheckinToday(events, date)) {
    return { events: triggerEvents };
  }
  const { events: checkinEvents } = completeCheckin(events, date, ports);
  return { events: [...triggerEvents, ...checkinEvents] };
}

// ---------------------------------------------------------------------------
// Shared guards
// ---------------------------------------------------------------------------

function requireHabit(events: readonly CompassEvent[], habitId: HabitId): HabitState {
  const states = buildHabitStates(events);
  const state = states.get(habitId);
  if (!state || state.lifecycle === 'archived') {
    throw domainError('habit_not_found', `habit ${habitId} not found`);
  }
  return state;
}

/**
 * Stricter than `requireHabit`: check-in logging commands (complete/skip/
 * clean/lapse) must target an ACTIVE habit -- a paused habit has left the
 * due list entirely (AC-B4) and logging against it would silently resurrect
 * it in weekly counts with no due-list evidence it happened. Lifecycle
 * commands (edit/adjust/archive) use the looser `requireHabit` since editing
 * a paused habit's schedule ahead of resuming it is a legitimate flow.
 */
function requireActiveHabit(events: readonly CompassEvent[], habitId: HabitId): HabitState {
  const state = requireHabit(events, habitId);
  if (state.lifecycle !== 'active') {
    throw domainError(
      'habit_not_found',
      `habit ${habitId} is not active (state: ${state.lifecycle})`,
    );
  }
  return state;
}
